import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, Valuables } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    withCurrentEncounterId,
    isEmptyEncounterId,
    currentEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { ValuablesApi } from '../../infrastructure';
import { ValuablesAction } from '../actions';
import { selectValuablesAll } from '../selectors';

/**
 * Valuables effects
 */
@Injectable()
export class ValuablesEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    selectValuablesAll$ = this.store.select(selectValuablesAll);

    /** Load Valuables */
    loadValuables$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ValuablesAction.loadValuables),
            withCurrentEncounterId(),
            concatMap(([, encounterId]) =>
                this.valuablesApi.getValuables(encounterId).pipe(
                    isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                    withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                    map(([data, changesMap, practitionersMap]) => {
                        const changes = changesMap['Valuables'] || [];

                        if (changes.length) {
                            data = applyInstancesChanges(data, changes) as Valuables[];
                        }

                        data = applyLastChangedByToPrescriptions(data, practitionersMap);

                        return ValuablesAction.loadValuablesSuccess({ data });
                    }),
                    catchError(error => of(ValuablesAction.loadValuablesFailure({ error }))),
                    takeUntil(isEmptyEncounterId(this.store)),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.succeededAction,
                ),
                withLatestFrom(this.selectValuablesAll$, this.changeLogMap$),
                tap(([, data, changesMap]) => {
                    const changes: ChangeLogEntry<Valuables>[] = changesMap['Valuables'] || [];

                    if (changes.length) {
                        const valuables = applyInstancesChanges(data, changes);

                        this.store.dispatch(ValuablesAction.upsertValuables({ data: valuables }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.selectValuablesAll$,
                    this.changeLogMap$,
                    this.practitionersFacade.practitionersMap$,
                ),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['Valuables'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            ValuablesAction.addValuablesChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     *
     * @param store
     * @param actions$
     * @param valuablesApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private valuablesApi: ValuablesApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
